import { handleServerError, isAuthError } from '@/lib/handle-server-error';
import { createTRPCClient, httpBatchLink, httpLink } from '@trpc/client';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import { nanoid } from 'nanoid';
import superjson from 'superjson';
import type { AppRouter } from '../../../server/src/routers/trpc-router';
import { apiEndpoints } from './env';

import type { QueryClient as TQueryClient } from '@tanstack/react-query' with { 'resolution-mode': 'require' };
import {
  QueryCache,
  QueryClient
} from '@tanstack/react-query';
import { clearAuthData, getAuthToken } from './auth';
import { getDevPlanOverride } from '@/utils/dev-tools';

export const getAuthHeaders = async () => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Request-Id': nanoid(),
    'X-Workspace-Id': localStorage.getItem('auth:activeWorkspaceId') ?? '',
  };

  const authToken = await getAuthToken()
  if (authToken?.token) {
    // console.log('Using auth token', authToken.token);
    headers['Authorization'] = `Bearer ${authToken.token}`;
  } else {
    console.warn('No auth token available in main process');
  }

  // Add dev plan override header (development only)
  const devPlanOverride = getDevPlanOverride();
  if (devPlanOverride) {
    headers['X-Dev-Plan-Override'] = devPlanOverride;
  }

  return headers;
};

export const queryClient: TQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        // Don't retry auth errors (401 Unauthorized, 403 Forbidden)
        if (isAuthError(error)) {
          return false;
        }

        return true;
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      // Use default staleTime (0) - let React Query handle intelligent caching
    },
    mutations: {
      onError: (error) => {
        // General error handling
        handleServerError(error);
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      handleServerError(error);
    },
  }),
}) as unknown as TQueryClient;

const isDevelopment = import.meta.env.DEV
const link = isDevelopment ? httpLink : httpBatchLink;

export const trpcClient = createTRPCClient<AppRouter>({
  links: [link({
    url: apiEndpoints.trpcApi,
    transformer: superjson,
    // Keep batched query URLs within safe limits to avoid upstream 414/connection resets
    maxURLLength: 2083,
    fetch: async (url, options) => {
      // First, make the standard fetch request with credentials
      const headers = await getAuthHeaders()
      const response = await fetch(url, {
        ...(options as RequestInit),
        credentials: "include", // Keep cookies for web app auth
        headers: {
          ...headers,
          ...(options?.headers as Record<string, string>),
        },
      });

      // Check for auth errors and handle them differently
      if (response.status === 401) {
        console.log('Auth error detected in TRPC fetch:', response.status, "headers", headers);

        // Clear all caches (localStorage, localforage, React Query)
        // This is async but we don't await to avoid blocking the response
        clearAuthData().catch(error => {
          console.error('Error clearing auth data on 401:', error);
        });

        const { pathname } = window.location;
        if (!pathname.includes('/login')) {
          // Use window.location instead of throwing a redirect
          // This is more reliable for auth redirects
          window.location.href = `/login?error=unauthenticated&redirect_uri=${encodeURIComponent(window.location.href)}`;

          // Return a fake response to avoid further processing
          // The page will reload with the redirect anyway
          return new Response(JSON.stringify({ error: "Authentication required" }), {
            status: response.status,
            headers: response.headers,
          });
        }
      }

      // For all other responses, just return as normal
      return response;
    },
  }),],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryClient: queryClient as any,
});

export type Inputs = inferRouterInputs<AppRouter>;
export type Outputs = inferRouterOutputs<AppRouter>;
